// Generated from scripts/product-badges.ts. Rebuild with pnpm scripts:build.
import { StandardEvents } from '@shopify/events';
/**
 * Product badges.
 *
 * The merchant defines the rules once, as blocks of the product badges section in the overlay group. That
 * section renders each rule as an inert `<template>` inside one `<wontio-badge-rules>` element. Every product
 * surface (card media, title, price, product page gallery, quick view) renders a `<wontio-badges>` host that
 * carries the product's facts as data attributes, with the slots the badges go into. Liquid owns the facts that
 * need Shopify's formatting (discount percent, money, stock policy); this module evaluates the rules against them,
 * clones the matching templates into the slots in block order and fills the text tokens with `textContent`.
 *
 * Nothing here fetches. After a variant selection the host copies the facts from the product section Shopify
 * re-rendered for that selection, the same document the price and media gallery read.
 */
const TRIGGERS = ['on_sale', 'sold_out', 'low_stock', 'new', 'tag', 'collection', 'metafield', 'activity'];
/** The product activity counts of the last 30 days the host carries, which an activity rule names as its match. */
const METRICS = ['sold', 'carted', 'wishlisted'];
const SLOTS = [
    'image-top-start',
    'image-top-end',
    'image-bottom-start',
    'image-bottom-end',
    'under-image',
    'before-title',
    'after-title',
    'after-price',
];
/** The attributes a host reads its facts from, in the order Liquid writes them. */
const FACT_ATTRIBUTES = [
    'data-badge-product',
    'data-available',
    'data-on-sale',
    'data-percent',
    'data-saved',
    'data-stock',
    'data-published',
    'data-tags',
    'data-collections',
    'data-metafield',
    'data-sold',
    'data-sold-text',
    'data-carted',
    'data-carted-text',
    'data-wishlisted',
    'data-wishlisted-text',
];
const SECONDS_PER_DAY = 86_400;
const TOKEN_PATTERN = /\[(percent|saved|stock|count)\]/g;
function isTrigger(value) {
    return TRIGGERS.some((trigger) => trigger === value);
}
function isMetric(value) {
    return METRICS.some((metric) => metric === value);
}
function readActivity(dataset) {
    const entry = (count, text) => ({ count: parseNumber(count) ?? 0, text: text ?? '' });
    return {
        sold: entry(dataset.sold, dataset.soldText),
        carted: entry(dataset.carted, dataset.cartedText),
        wishlisted: entry(dataset.wishlisted, dataset.wishlistedText),
    };
}
function isSlot(value) {
    return SLOTS.some((slot) => slot === value);
}
function normalize(value) {
    return value.trim().toLowerCase();
}
function parseNumber(value) {
    if (value === undefined || value.trim() === '')
        return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}
function parseList(value) {
    if (!value)
        return new Set();
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
            const items = parsed;
            return new Set(items.filter((item) => typeof item === 'string').map(normalize));
        }
    }
    catch {
        // A malformed list means the host has no values to match.
    }
    return new Set();
}
function readFacts(host) {
    const { dataset } = host;
    return {
        available: dataset.available !== 'false',
        onSale: dataset.onSale === 'true',
        percent: parseNumber(dataset.percent) ?? 0,
        tokens: { percent: dataset.percent ?? '', saved: dataset.saved ?? '', stock: dataset.stock ?? '', count: '' },
        stock: parseNumber(dataset.stock),
        publishedAt: parseNumber(dataset.published),
        tags: parseList(dataset.tags),
        collections: parseList(dataset.collections),
        metafield: new Set([...parseList(dataset.metafield)].filter((value) => value !== '')),
        activity: readActivity(dataset),
    };
}
function matches(rule, facts, nowSeconds) {
    switch (rule.trigger) {
        case 'on_sale':
            return facts.onSale && facts.percent >= rule.threshold;
        case 'sold_out':
            return !facts.available;
        case 'low_stock':
            return facts.stock !== undefined && facts.stock > 0 && facts.stock < rule.threshold;
        case 'new': {
            if (facts.publishedAt === undefined)
                return false;
            const age = nowSeconds - facts.publishedAt;
            return age >= 0 && age <= rule.threshold * SECONDS_PER_DAY;
        }
        case 'tag':
            return facts.tags.has(rule.match);
        case 'collection':
            return facts.collections.has(rule.match);
        case 'metafield':
            return rule.match === '' ? facts.metafield.size > 0 : facts.metafield.has(rule.match);
        case 'activity': {
            if (!isMetric(rule.match))
                return false;
            const { count } = facts.activity[rule.match];
            return count > 0 && count >= rule.threshold;
        }
        default: {
            const exhaustive = rule.trigger;
            return exhaustive;
        }
    }
}
/** The tokens of one rule: the host's, and for an activity rule [count] as the printed text of its count. */
function tokensOf(rule, facts) {
    if (rule.trigger !== 'activity' || !isMetric(rule.match))
        return facts.tokens;
    return { ...facts.tokens, count: facts.activity[rule.match].text };
}
/** Substitutes the tokens a product does not supply with nothing, never with the literal token. */
function fillTokens(text, tokens) {
    return text
        .replace(TOKEN_PATTERN, (_match, name) => tokens[name])
        .replace(/\s+/g, ' ')
        .trim();
}
function parseRule(template) {
    const { badgeRule: id, trigger, threshold, match, cardSlot, pageSlot } = template.dataset;
    if (!id || !isTrigger(trigger))
        return undefined;
    const card = isSlot(cardSlot) ? cardSlot : undefined;
    const page = isSlot(pageSlot) ? pageSlot : undefined;
    if (!card && !page)
        return undefined;
    return {
        id,
        trigger,
        threshold: parseNumber(threshold) ?? 0,
        match: normalize(match ?? ''),
        cardSlot: card,
        pageSlot: page,
        text: template.content.querySelector('[data-badge-text]')?.textContent ?? undefined,
        template,
    };
}
// The rules are parsed once per load of the rules element and shared by every host.
let rules = [];
let perSlotLimit = 1;
let rulesSignature;
// Part of every rendered badge's key, so a host re-renders badges whose rule changed but kept its id.
let rulesVersion = 0;
const hosts = new Set();
/**
 * Reads the rules element the page currently holds. It is safe to call from any lifecycle point: a call that
 * finds the same element content does nothing, and a call after the element left the page clears the rules, so
 * the editor's section reload (the old element leaves, the new one arrives) ends with the new rules in place.
 */
function syncRules() {
    const element = document.querySelector('wontio-badge-rules');
    const signature = element ? element.outerHTML : '';
    if (signature === rulesSignature)
        return;
    rulesSignature = signature;
    rulesVersion += 1;
    rules = element
        ? [...element.querySelectorAll('template[data-badge-rule]')]
            .map(parseRule)
            .filter((rule) => rule !== undefined)
        : [];
    perSlotLimit = Math.max(1, parseNumber(element?.dataset.limit) ?? 1);
    for (const host of hosts)
        host.refresh();
}
function isProductSelect(event) {
    return 'promise' in event && event.promise instanceof Promise;
}
function isAbort(error) {
    return error instanceof Error && error.name === 'AbortError';
}
class WontioBadgeRules extends HTMLElement {
    connectedCallback() {
        syncRules();
    }
    disconnectedCallback() {
        syncRules();
    }
}
/**
 * A product surface's badge host. It owns the badges inside its slots: it evaluates the rules on connect, when
 * its facts change, when the rules change and when a morph strips the badges it rendered.
 */
class WontioBadges extends HTMLElement {
    static observedAttributes = FACT_ATTRIBUTES;
    #lifetime;
    #observer;
    #connected = false;
    #queued = false;
    #selection = 0;
    connectedCallback() {
        this.#lifetime?.abort();
        this.#observer?.disconnect();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        this.#connected = true;
        hosts.add(this);
        syncRules();
        const scope = this.closest('.shopify-section, dialog') ?? document;
        scope.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal: lifetime.signal });
        // A morph of the surrounding fragment (filters, load more, quick view) rebuilds the empty slots Liquid
        // rendered and removes the badges inside them, so the host renders again when its subtree changes.
        this.#observer = new MutationObserver(() => this.refresh());
        this.#observer.observe(this, { childList: true, subtree: true });
        this.#render();
    }
    disconnectedCallback() {
        this.#connected = false;
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        this.#observer?.disconnect();
        this.#observer = undefined;
        hosts.delete(this);
    }
    attributeChangedCallback(_name, previous, next) {
        if (previous !== next)
            this.refresh();
    }
    /** Queues one evaluation; any number of calls in the same task cost one render. */
    refresh() {
        if (!this.#connected || this.#queued)
            return;
        this.#queued = true;
        queueMicrotask(() => {
            this.#queued = false;
            if (this.#connected)
                this.#render();
        });
    }
    #render() {
        const facts = readFacts(this);
        const context = this.closest('product-card') ? 'card' : 'page';
        const nowSeconds = Date.now() / 1000;
        const slots = new Map();
        for (const element of this.querySelectorAll('[data-badge-slot]')) {
            const name = element.dataset.badgeSlot;
            if (isSlot(name))
                slots.set(name, element);
        }
        const placements = new Map();
        for (const rule of rules) {
            const slot = context === 'card' ? rule.cardSlot : rule.pageSlot;
            if (!slot || !slots.has(slot) || !matches(rule, facts, nowSeconds))
                continue;
            const text = rule.text === undefined ? undefined : fillTokens(rule.text, tokensOf(rule, facts));
            if (text === '')
                continue;
            const placed = placements.get(slot) ?? [];
            if (placed.length >= perSlotLimit)
                continue;
            placed.push({ rule, text });
            placements.set(slot, placed);
        }
        const corners = [];
        for (const [name, element] of slots) {
            const wanted = placements.get(name) ?? [];
            const wantedKeys = wanted.map(({ rule, text }) => badgeKey(rule, text));
            const renderedKeys = [...element.children].map((child) => (child instanceof HTMLElement ? child.dataset.badgeKey : undefined));
            if (wantedKeys.length !== renderedKeys.length || wantedKeys.some((key, index) => key !== renderedKeys[index])) {
                element.replaceChildren(...wanted.flatMap(({ rule, text }) => createBadge(rule, text) ?? []));
            }
            if (name.startsWith('image-') && wanted.length)
                corners.push(name.slice('image-'.length));
        }
        // Styles that make room for the occupied corners (a product without media shows its title there) read this.
        if (corners.length)
            this.dataset.corners = corners.join(' ');
        else
            delete this.dataset.corners;
        // Drop the records of this render so only changes made by someone else wake the observer.
        this.#observer?.takeRecords();
    }
    /** Re-evaluates with the facts of the product section Shopify rendered for the selected variant. */
    #onProductSelect = (event) => {
        if (!isProductSelect(event) || !(event.target instanceof Element))
            return;
        const card = this.closest('product-card');
        const source = event.target.closest('product-card');
        // A card's swatches update that card only; the product page and quick view ignore swatches of other cards.
        if (card ? source !== card : source)
            return;
        const selection = ++this.#selection;
        event.promise
            .then(({ detail }) => {
            if (selection !== this.#selection || !this.#connected || !detail?.html)
                return;
            this.#adoptFacts(detail.html, detail.newProduct?.id ?? detail.productId ?? this.dataset.badgeProduct);
        })
            .catch((error) => {
            if (!isAbort(error))
                console.warn('[product-badges] Event promise rejected:', error);
        });
    };
    #adoptFacts(html, productId) {
        const inCard = this.closest('product-card') !== null;
        const candidates = [...html.querySelectorAll('wontio-badges')].filter((candidate) => (candidate.closest('product-card') !== null) === inCard);
        const source = candidates.find((candidate) => candidate.dataset.badgeProduct === productId) ?? candidates[0];
        if (!source)
            return;
        for (const name of FACT_ATTRIBUTES) {
            const value = source.getAttribute(name);
            if (value === null)
                this.removeAttribute(name);
            else if (this.getAttribute(name) !== value)
                this.setAttribute(name, value);
        }
    }
}
function badgeKey(rule, text) {
    return `${rulesVersion}\u0000${rule.id}\u0000${text ?? ''}`;
}
function createBadge(rule, text) {
    const fragment = document.importNode(rule.template.content, true);
    const badge = fragment.firstElementChild;
    if (!(badge instanceof HTMLElement))
        return undefined;
    if (text !== undefined) {
        const label = badge.querySelector('[data-badge-text]');
        if (label)
            label.textContent = text;
    }
    badge.dataset.badgeKey = badgeKey(rule, text);
    return badge;
}
if (!customElements.get('wontio-badge-rules'))
    customElements.define('wontio-badge-rules', WontioBadgeRules);
if (!customElements.get('wontio-badges'))
    customElements.define('wontio-badges', WontioBadges);
// The editor re-renders the rules section in place; the element lifecycle covers the swap, and this covers a
// reload that keeps the element and changes its templates.
document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element && event.target.querySelector('wontio-badge-rules'))
        syncRules();
});
